/**
 * Global API utility functions for working with AppClust API
 */

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

/**
 * Save data to any module
 */
export async function saveToModule<T = any>(
  moduleName: string,
  data: any
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`/api/v1/modules/${moduleName}/saveToDB`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Failed to save data to ${moduleName}`);
    }

    return result;
  } catch (error) {
    console.error(`Error saving to module ${moduleName}:`, error);
    throw error;
  }
}

/**
 * List data from any module
 */
export async function listFromModule<T = any>(
  moduleName: string,
  params?: Record<string, string>
): Promise<ApiResponse<T>> {
  try {
    const queryParams = params ? new URLSearchParams(params).toString() : '';
    const url = `/api/v1/modules/${moduleName}/list${queryParams ? `?${queryParams}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Failed to fetch data from ${moduleName}`);
    }

    return result;
  } catch (error) {
    console.error(`Error listing from module ${moduleName}:`, error);
    throw error;
  }
}

/**
 * Save booking data specifically
 */
export async function saveBooking(bookingData: any): Promise<ApiResponse> {
  // Transform the data to match the expected API structure
  const transformedData = {
    bookingDate: bookingData.bookingDate,
    childName: bookingData.childName,
    childAge: bookingData.childAge,
    dateOfBirth: bookingData.dateOfBirth,
    gender: bookingData.gender,
    language: bookingData.language,
    nationality: bookingData.nationality,
    guardianName: bookingData.parentGuardianName,
    phone: bookingData.phone,
    email: bookingData.email,
    leadSource: bookingData.leadSource,
    leadDetails: bookingData.leadDetails || bookingData.leadSource,
    majorConcern: bookingData.majorConcern
  };

  // Debug: Log the transformed data
  console.log('API transformed data:', transformedData);

  return saveToModule('bookings', {
    DATAINFO: JSON.stringify(transformedData)
  });
}

/**
 * List bookings
 */
export async function listBookings(params?: Record<string, string>): Promise<ApiResponse> {
  return listFromModule('bookings', params);
}

/**
 * Get booking by permalink ID
 */
export async function getBookingByPermalink(objID: string): Promise<ApiResponse> {
  try {
    const response = await fetch(`/api/v1/bookings/${objID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Failed to fetch booking data`);
    }

    return result;
  } catch (error) {
    console.error(`Error fetching booking by permalink ${objID}:`, error);
    throw error;
  }
}

/**
 * Get any record by permalink ID (generic function for any module)
 */
export async function getRecordByPermalink(uniqID: string): Promise<ApiResponse> {
  try {
    const response = await fetch(`/api/v1/permalinks/${uniqID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `Failed to fetch data`);
    }

    return result;
  } catch (error) {
    console.error(`Error fetching record by permalink ${uniqID}:`, error);
    throw error;
  }
} 